"use client";

import { useEffect } from "react";
import { getAcquisitionContext } from "@/lib/acquisition";

const POSTHOG_FALLBACK_DELAY_MS = 0;
const POSTHOG_IDLE_TIMEOUT_MS = 1_000;

export function PostHogBootstrap(): null {
  useEffect(() => {
    // Capture the landing context before client-side navigation or SDK loading.
    getAcquisitionContext();
    let disposed = false;
    let initializationScheduled = false;
    let idleCallbackId: number | null = null;
    let idleFallbackId: number | null = null;
    let fallbackDelayId: number | null = null;

    const initializePostHog = () => {
      if (disposed) return;

      void import("@/lib/posthog-client")
        .then((module) => module.initializePostHog())
        .catch(() => {
          // Analytics must never affect page behavior.
        });
    };

    const removeEngagementListeners = () => {
      window.removeEventListener("pointerdown", scheduleInitialization);
      window.removeEventListener("keydown", scheduleInitialization);
    };

    function scheduleInitialization() {
      if (initializationScheduled || disposed) return;
      initializationScheduled = true;
      removeEngagementListeners();

      if (fallbackDelayId !== null) {
        window.clearTimeout(fallbackDelayId);
        fallbackDelayId = null;
      }

      if (typeof window.requestIdleCallback === "function") {
        idleCallbackId = window.requestIdleCallback(initializePostHog, {
          timeout: POSTHOG_IDLE_TIMEOUT_MS,
        });
        return;
      }

      idleFallbackId = window.setTimeout(initializePostHog, 0);
    }

    // Load after rendering, with a short idle timeout. Waiting for engagement
    // or 15 seconds systematically missed passive visits and short signup paths.
    window.addEventListener("pointerdown", scheduleInitialization, {
      once: true,
      passive: true,
    });
    window.addEventListener("keydown", scheduleInitialization, { once: true });
    fallbackDelayId = window.setTimeout(
      scheduleInitialization,
      POSTHOG_FALLBACK_DELAY_MS,
    );

    return () => {
      disposed = true;
      removeEngagementListeners();

      if (fallbackDelayId !== null) {
        window.clearTimeout(fallbackDelayId);
      }
      if (idleCallbackId !== null) {
        window.cancelIdleCallback(idleCallbackId);
      }
      if (idleFallbackId !== null) {
        window.clearTimeout(idleFallbackId);
      }
    };
  }, []);

  return null;
}

export function PostHogProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <PostHogBootstrap />
      {children}
    </>
  );
}
